import {
  AlertTriangle,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader2,
  Save,
  SkipForward,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/services/api";
import type { TagResponse, Topic } from "@/types";

const MULTI_DIMS = [
  { key: "topic_area", label: "Topic Area", required: true },
  { key: "angle_type", label: "Angle Type", required: true },
  { key: "sector", label: "Sector", required: false },
  { key: "geography", label: "Geography", required: false },
  { key: "event_type", label: "Event Type", required: false },
  { key: "asset_class", label: "Asset Class", required: false },
  { key: "theme", label: "Theme", required: false },
] as const;

const SINGLE_DIMS = [
  { key: "time_mode", label: "Time Mode" },
  { key: "difficulty", label: "Difficulty" },
] as const;

interface TopicReviewQueueProps {
  topics: Topic[];
  currentIndex: number;
  onApprove: (topic: Topic, editedTitle?: string, editedDescription?: string) => void;
  onSkip: () => void;
  onSave: (topic: Topic, title: string, description: string) => Promise<void>;
  onDelete: (topic: Topic) => void;
  isProcessing?: boolean;
  topicAreas: TagResponse[];
  allTagsByGroup?: Record<string, TagResponse[]>;
}

export function TopicReviewQueue({
  topics,
  currentIndex,
  onApprove,
  onSkip,
  onSave,
  onDelete,
  isProcessing = false,
  topicAreas,
  allTagsByGroup = {},
}: TopicReviewQueueProps) {
  const currentTopic = topics[currentIndex];
  const total = topics.length;

  // Local editing state
  const [editedTitle, setEditedTitle] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [editedContext, setEditedContext] = useState<string>("");
  const [editedCoreQuestion, setEditedCoreQuestion] = useState<string>("");
  const [editedGuidance, setEditedGuidance] = useState<string>("");
  const [editedEvidenceHints, setEditedEvidenceHints] = useState<string>("");
  const [selectedTopicAreaIds, setSelectedTopicAreaIds] = useState<string[]>([]);
  // Classification section
  const [classificationOpen, setClassificationOpen] = useState(true);
  const [selectedTagIds, setSelectedTagIds] = useState<Record<string, string[]>>({});
  const [selectedSingleTagIds, setSelectedSingleTagIds] = useState<Record<string, string | null>>(
    {}
  );
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset edited values when topic changes
  useEffect(() => {
    if (currentTopic) {
      setEditedTitle(currentTopic.title);
      setEditedDescription(currentTopic.description);
      setEditedContext(currentTopic.context ?? "");
      setEditedCoreQuestion(currentTopic.core_question ?? "");
      setEditedGuidance(currentTopic.guidance ?? "");
      setEditedEvidenceHints(currentTopic.evidence_hints ?? "");
      const topicTags = currentTopic.topic_tags ?? currentTopic.tags;
      setSelectedTopicAreaIds(
        topicTags.filter((t) => t.tag_group === "topic_area").map((t) => t.id)
      );
      // Initialize multi-dim tag selections
      const tagsByGroup: Record<string, string[]> = {};
      for (const dim of MULTI_DIMS) {
        tagsByGroup[dim.key] = topicTags.filter((t) => t.tag_group === dim.key).map((t) => t.id);
      }
      setSelectedTagIds(tagsByGroup);
      // Initialize single-dim selections
      setSelectedSingleTagIds({
        time_mode: currentTopic.time_mode_id ?? null,
        difficulty: currentTopic.difficulty_id ?? null,
      });
      setShowSkipConfirm(false);
      setSaveError(false);
      setJustSaved(false);
    }
  }, [currentTopic]);

  // Check if there are unsaved changes
  const hasUnsavedChanges =
    currentTopic &&
    (editedTitle !== currentTopic.title || editedDescription !== currentTopic.description);

  // Save a single field directly (for structured fields that don't flow through onSave)
  const saveField = useCallback(
    async (field: Record<string, string | null>) => {
      if (!currentTopic) return;
      setIsSaving(true);
      setSaveError(false);
      try {
        await api.updateTopic(currentTopic.id, field);
        setJustSaved(true);
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => setJustSaved(false), 2000);
      } catch {
        setSaveError(true);
      } finally {
        setIsSaving(false);
      }
    },
    [currentTopic]
  );

  // Auto-save on blur with debounce
  const triggerAutoSave = useCallback(async () => {
    if (!currentTopic || !hasUnsavedChanges || isSaving) return;

    setIsSaving(true);
    setSaveError(false);
    try {
      await onSave(currentTopic, editedTitle, editedDescription);
      setJustSaved(true);
      // Clear the "saved" indicator after 2 seconds
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => setJustSaved(false), 2000);
    } catch {
      setSaveError(true);
    } finally {
      setIsSaving(false);
    }
  }, [currentTopic, hasUnsavedChanges, isSaving, onSave, editedTitle, editedDescription]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const hasTopicArea = selectedTopicAreaIds.length > 0;

  const handleTagToggle = useCallback(
    async (tagId: string) => {
      if (!currentTopic) return;
      const next = selectedTopicAreaIds.includes(tagId)
        ? selectedTopicAreaIds.filter((id) => id !== tagId)
        : [...selectedTopicAreaIds, tagId];
      setSelectedTopicAreaIds(next);
      try {
        await api.updateTopic(currentTopic.id, { topic_area_ids: next });
      } catch {
        setSelectedTopicAreaIds(selectedTopicAreaIds);
      }
    },
    [currentTopic, selectedTopicAreaIds]
  );

  const handleMultiDimToggle = useCallback(
    async (dimKey: string, tagId: string) => {
      if (!currentTopic) return;
      const prev = selectedTagIds[dimKey] ?? [];
      const next = prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId];
      setSelectedTagIds((s) => ({ ...s, [dimKey]: next }));
      try {
        // Collect all multi-dim IDs from current state + this change
        const allTagIds: string[] = [];
        for (const dim of MULTI_DIMS) {
          if (dim.key === dimKey) {
            allTagIds.push(...next);
          } else {
            allTagIds.push(...(selectedTagIds[dim.key] ?? []));
          }
        }
        await api.updateTopic(currentTopic.id, { tag_ids: allTagIds });
      } catch {
        setSelectedTagIds((s) => ({ ...s, [dimKey]: prev }));
      }
    },
    [currentTopic, selectedTagIds]
  );

  const handleSingleDimChange = useCallback(
    async (dimKey: "time_mode_id" | "difficulty_id", tagId: string | null) => {
      if (!currentTopic) return;
      const prev = selectedSingleTagIds[dimKey.replace("_id", "")];
      setSelectedSingleTagIds((s) => ({ ...s, [dimKey.replace("_id", "")]: tagId }));
      try {
        await api.updateTopic(currentTopic.id, { [dimKey]: tagId });
      } catch {
        setSelectedSingleTagIds((s) => ({ ...s, [dimKey.replace("_id", "")]: prev ?? null }));
      }
    },
    [currentTopic, selectedSingleTagIds]
  );

  const [computedEvidenceRichness, setComputedEvidenceRichness] = useState<string | null>(null);

  useEffect(() => {
    const angleTypeIds = selectedTagIds.angle_type ?? [];
    const difficultyId = selectedSingleTagIds.difficulty ?? null;
    if (angleTypeIds.length === 0) {
      setComputedEvidenceRichness(null);
      return;
    }
    api.getEvidenceRichness(angleTypeIds, difficultyId).then((res) => {
      setComputedEvidenceRichness(res.value);
    });
  }, [selectedTagIds, selectedSingleTagIds]);

  const handleApprove = useCallback(() => {
    if (currentTopic && !isProcessing && hasTopicArea) {
      onApprove(currentTopic, editedTitle, editedDescription);
    }
  }, [currentTopic, isProcessing, hasTopicArea, onApprove, editedTitle, editedDescription]);

  const handleSkipClick = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowSkipConfirm(true);
    } else {
      onSkip();
    }
  }, [hasUnsavedChanges, onSkip]);

  const handleConfirmSkip = useCallback(() => {
    setShowSkipConfirm(false);
    onSkip();
  }, [onSkip]);

  const handleCancelSkip = useCallback(() => {
    setShowSkipConfirm(false);
  }, []);

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (currentTopic) {
      setShowDeleteConfirm(false);
      onDelete(currentTopic);
    }
  }, [currentTopic, onDelete]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if user is in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      switch (e.key) {
        case "Enter":
          e.preventDefault();
          handleApprove();
          break;
        case "s":
        case "S":
          e.preventDefault();
          handleSkipClick();
          break;
        case "Escape":
          if (showSkipConfirm) {
            e.preventDefault();
            setShowSkipConfirm(false);
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleApprove, handleSkipClick, showSkipConfirm]);

  if (total === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">All Caught Up!</h2>
        <p className="text-gray-600">
          No draft topics to review. Generate more topics or check back later.
        </p>
      </div>
    );
  }

  if (!currentTopic) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Review Queue</h3>
              <p className="text-sm text-gray-500">
                {total} draft{total !== 1 ? "s" : ""} pending review
              </p>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">Enter</kbd>{" "}
            to approve, <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">S</kbd>{" "}
            to skip
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-8">
        {/* Topic area warning */}
        {!hasTopicArea && (
          <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-500" />
            Select at least one topic area to approve.
          </div>
        )}

        {/* Editable Title */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
          <input
            type="text"
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            onBlur={triggerAutoSave}
            className="w-full px-4 py-3 text-xl font-bold text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ring focus:border-ring outline-none"
            placeholder="Topic title"
          />
        </div>

        {/* Editable Description */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            value={editedDescription}
            onChange={(e) => setEditedDescription(e.target.value)}
            onBlur={triggerAutoSave}
            rows={4}
            className="w-full px-4 py-3 text-gray-600 leading-relaxed border border-gray-300 rounded-lg focus:ring-2 focus:ring-ring focus:border-ring outline-none resize-none"
            placeholder="Topic description"
          />
        </div>

        {/* Archetype badge (task 7.5) */}
        {currentTopic.archetype_name && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Archetype</label>
            <span className="inline-flex items-center px-3 py-1.5 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
              {currentTopic.archetype_name}
            </span>
          </div>
        )}

        {/* A1-A8 reasoning profile (task 7.6) */}
        {currentTopic.a_targets && currentTopic.a_targets.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              A-Component Targets
            </label>
            <div className="flex flex-wrap gap-2">
              {currentTopic.a_targets.map((t) => (
                <span
                  key={t.component_code}
                  title={t.component_name}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                    t.strength === "primary"
                      ? "bg-indigo-600 text-white"
                      : "border border-indigo-400 text-indigo-700 bg-white"
                  }`}
                >
                  {t.component_code}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Context (task 7.1) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Context</label>
          <textarea
            value={editedContext}
            onChange={(e) => setEditedContext(e.target.value)}
            onBlur={() => saveField({ context: editedContext || null })}
            rows={3}
            className="w-full px-4 py-3 text-gray-600 leading-relaxed border border-gray-300 rounded-lg focus:ring-2 focus:ring-ring focus:border-ring outline-none resize-none"
            placeholder="Market or macro context for this topic…"
          />
        </div>

        {/* Core question (task 7.2) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Core Question</label>
          <textarea
            value={editedCoreQuestion}
            onChange={(e) => setEditedCoreQuestion(e.target.value)}
            onBlur={() => saveField({ core_question: editedCoreQuestion || null })}
            rows={2}
            className="w-full px-4 py-3 text-gray-600 leading-relaxed border border-gray-300 rounded-lg focus:ring-2 focus:ring-ring focus:border-ring outline-none resize-none"
            placeholder="The central analytical question the candidate must answer…"
          />
        </div>

        {/* Guidance (task 7.3) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Guidance</label>
          <textarea
            value={editedGuidance}
            onChange={(e) => setEditedGuidance(e.target.value)}
            onBlur={() => saveField({ guidance: editedGuidance || null })}
            rows={4}
            className="w-full px-4 py-3 text-gray-600 leading-relaxed border border-gray-300 rounded-lg focus:ring-2 focus:ring-ring focus:border-ring outline-none resize-none"
            placeholder="Approach guidance and analytical framework hints…"
          />
        </div>

        {/* Evidence hints (task 7.4) */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Evidence Hints</label>
          <textarea
            value={editedEvidenceHints}
            onChange={(e) => setEditedEvidenceHints(e.target.value)}
            onBlur={() => saveField({ evidence_hints: editedEvidenceHints || null })}
            rows={3}
            className="w-full px-4 py-3 text-gray-600 leading-relaxed border border-gray-300 rounded-lg focus:ring-2 focus:ring-ring focus:border-ring outline-none resize-none"
            placeholder="Data sources, charts, or evidence the candidate should reference…"
          />
        </div>

        {/* Classification section */}
        {Object.keys(allTagsByGroup).length > 0 && (
          <div className="mb-6 border border-gray-200 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setClassificationOpen((o) => !o)}
              className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-semibold text-gray-700"
            >
              Classification
              {classificationOpen ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>
            {classificationOpen && (
              <div className="p-5 space-y-5">
                {/* Multi-value dimensions */}
                {MULTI_DIMS.map((dim) => {
                  const tags = allTagsByGroup[dim.key] ?? [];
                  if (tags.length === 0) return null;
                  const selectedIds =
                    dim.key === "topic_area"
                      ? selectedTopicAreaIds
                      : (selectedTagIds[dim.key] ?? []);
                  return (
                    <div key={dim.key}>
                      <label className="block text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
                        {dim.label}
                        {dim.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {tags.map((tag) => {
                          const selected = selectedIds.includes(tag.id);
                          return (
                            <button
                              key={tag.id}
                              type="button"
                              onClick={() =>
                                dim.key === "topic_area"
                                  ? handleTagToggle(tag.id)
                                  : handleMultiDimToggle(dim.key, tag.id)
                              }
                              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                selected
                                  ? dim.key === "topic_area"
                                    ? "bg-primary text-white hover:bg-primary/90"
                                    : dim.key === "angle_type"
                                      ? "bg-purple-500 text-white hover:bg-purple-600"
                                      : "bg-indigo-500 text-white hover:bg-indigo-600"
                                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                              }`}
                            >
                              {tag.display_name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Single-value dimensions: time_mode, difficulty */}
                {SINGLE_DIMS.map((dim) => {
                  const tags = allTagsByGroup[dim.key] ?? [];
                  if (tags.length === 0) return null;
                  const currentId = selectedSingleTagIds[dim.key] ?? null;
                  return (
                    <div key={dim.key}>
                      <label className="block text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
                        {dim.label}
                      </label>
                      <select
                        value={currentId ?? ""}
                        onChange={(e) =>
                          handleSingleDimChange(
                            `${dim.key}_id` as "time_mode_id" | "difficulty_id",
                            e.target.value || null
                          )
                        }
                        className="w-full px-3 py-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-ring focus:border-ring outline-none"
                      >
                        <option value="">— not set —</option>
                        {tags.map((tag) => (
                          <option key={tag.id} value={tag.id}>
                            {tag.display_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}

                {/* evidence_richness — read-only, auto-computed from angle_type + difficulty */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
                    Evidence Richness
                  </label>
                  {computedEvidenceRichness ? (
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                          computedEvidenceRichness === "Low"
                            ? "bg-amber-100 text-amber-700"
                            : computedEvidenceRichness === "High"
                              ? "bg-green-100 text-green-700"
                              : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {computedEvidenceRichness === "Low" && "⚠ "}
                        {computedEvidenceRichness}
                      </span>
                      <span className="text-xs text-gray-400">
                        auto-computed from angle type + difficulty
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400 italic">Set angle type to compute</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Topic area chips (legacy — kept for topics without full classification) */}
        {Object.keys(allTagsByGroup).length === 0 && topicAreas.length > 0 && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Topic Area <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {topicAreas.map((tag) => {
                const selected = selectedTopicAreaIds.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleTagToggle(tag.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selected
                        ? "bg-primary text-white hover:bg-primary/90"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {tag.display_name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <button
            onClick={handleDeleteClick}
            disabled={isProcessing || isSaving}
            className="flex items-center gap-2 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium"
          >
            <Trash2 className="w-5 h-5" />
            Delete
          </button>
          <button
            onClick={handleSkipClick}
            disabled={isProcessing || isSaving}
            className="flex items-center gap-2 px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
          >
            <SkipForward className="w-5 h-5" />
            Skip
          </button>
          <button
            onClick={handleApprove}
            disabled={isProcessing || isSaving || !hasTopicArea}
            title={!hasTopicArea ? "Add a topic area before approving" : undefined}
            className="flex items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 disabled:bg-green-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
          >
            {isProcessing ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <CheckCircle className="w-5 h-5" />
            )}
            Approve
          </button>
        </div>

        {/* Status bar */}
        <div className="flex items-center justify-center gap-3 pt-4 border-t border-gray-100">
          <span className="text-gray-500 text-sm">
            {currentIndex + 1} of {total} remaining
          </span>
          {isSaving && (
            <span className="flex items-center gap-1.5 text-gray-500 text-sm">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Saving...
            </span>
          )}
          {justSaved && !hasUnsavedChanges && (
            <span className="flex items-center gap-1.5 text-green-600 text-sm">
              <Check className="w-3.5 h-3.5" />
              Saved
            </span>
          )}
          {saveError && (
            <span className="flex items-center gap-2 text-amber-600 text-sm">
              • Failed to save
              <button
                onClick={triggerAutoSave}
                disabled={isSaving}
                className="flex items-center gap-1 px-2 py-1 bg-amber-100 hover:bg-amber-200 rounded text-amber-700 font-medium text-xs"
              >
                <Save className="w-3 h-3" />
                Retry
              </button>
            </span>
          )}
        </div>
      </div>

      {/* Skip Confirmation Modal */}
      {showSkipConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Unsaved Changes</h3>
            </div>
            <p className="text-gray-600 mb-6">
              You have unsaved changes to this topic. Are you sure you want to skip without saving?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelSkip}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSkip}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors font-medium"
              >
                Skip Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Topic</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this topic? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
